import { Moment } from 'moment'

import { ConfigFilterItem, ConfigItem } from 'Core/interfaces'

import { ProcessingCenter } from './ProcessingCenter'

export enum OutboundTrailerSearchResultFields {
	searchByTrailerNumber = 'searchByTrailerNumber',
	searchBySealNumber = 'searchBySealNumber',
	searchByExportationNumber = 'searchByExportationNumber',
	searchBySupplierLocationId = 'searchBySupplierLocationId',
	searchBySupplierLocationName = 'searchBySupplierLocationName',
	searchByFolioNumber = 'searchByFolioNumber'
}

export enum OutboundTrailerFilterFields {
	departureDate = 'departureDate',
	processingCenterId = 'processingCenterId',
	statusId = 'statusId'
}

export enum OutboundTrailerFields {
	id = 'id',
	item = 'item',
	status = 'status',
	billBales = 'billBales',
	sealNumber = 'sealNumber',
	transporter = 'transporter',
	departureDate = 'departureDate',
	trailerNumber = 'trailerNumber',
	processingCenter = 'processingCenter',
	supplierLocation = 'supplierLocation',
	exportationNumber = 'exportationNumber',
	serviceOccurence = 'serviceOccurence',
	baleBillingReport = 'baleBillingReport'
}

export enum OutboundFormFields {
	processingCenterId = 'processingCenterId',
	trailerNumber = 'trailerNumber',
	sealNumber = 'sealNumber',
	exportationNumber = 'exportationNumber',
	departureDate = 'departureDate',
	supplierLocationId = 'supplierLocationId',
	transporterId = 'transporterId',
	trailerStatus = 'trailerStatus'
}

export enum TrailerStatus {
	buildingLoad = 'building load',
	shipped = 'shipped',
	finalized = 'finalized',
	cancelled = 'cancelled'
}

export enum LoadDetailsSearchFields {
	baleNumbers = 'baleNumbers',
	folioNumbers = 'folioNumbers',
	inboundSealNumbers = 'inboundSealNumbers'
}

export enum LoadDetailsFields {
	items = 'items'
}

export enum LoadDetailsTableFields {
	id = 'id',
	baleId = 'baleId',
	itemId = 'itemId',
	baleNumber = 'baleNumber',
	folioNumber = 'folioNumber',
	partialNumber = 'partialNumber',
	inboundSealNumber = 'inboundSealNumber',
	baleDate = 'baleDate',
	materialName = 'materialName',
	item = 'item',
	kilograms = 'kilograms',
	loadDetailsId = 'loadDetailsId',
	isMatchedUoM = 'isMatchedUoM'
}
export enum LoadDetailsFilterFields {
	serviceSiteId = 'serviceSiteId',
	materialId = 'materialId',
	baleDate = 'baleDate'
}

export declare namespace OutboundTrailer {
	interface Basic {
		[OutboundTrailerFields.id]: number
		[OutboundTrailerFields.trailerNumber]: string | null
		[OutboundTrailerFields.sealNumber]: string | null
		[OutboundTrailerFields.exportationNumber]: string | null
		[OutboundTrailerFields.departureDate]: string
		[OutboundTrailerFields.supplierLocation]: Entity
		[OutboundTrailerFields.transporter]: Entity
		[OutboundTrailerFields.processingCenter]: Entity | null
		[OutboundTrailerFields.status]: Entity<TrailerStatus> | null
	}

	interface ExtendedBasic extends Basic {
		[OutboundTrailerFields.item]: Entity | null
		[OutboundTrailerFields.billBales]: boolean
		[OutboundTrailerFields.baleBillingReport]: boolean
		[OutboundTrailerFields.serviceOccurence]: number | null
	}

	namespace Form {
		interface Read {
			[OutboundTrailerFields.id]: BasicString
			[OutboundTrailerFields.sealNumber]: BasicString
			[OutboundTrailerFields.serviceOccurence]: BasicString
			[OutboundTrailerFields.departureDate]: BasicString
			[OutboundTrailerFields.status]: BasicString
			[OutboundTrailerFields.item]: BasicString
		}
		interface Generic {
			[OutboundFormFields.processingCenterId]: number | null
			[OutboundFormFields.trailerNumber]: BasicString
			[OutboundFormFields.sealNumber]: BasicString
			[OutboundFormFields.exportationNumber]: BasicString
			[OutboundFormFields.departureDate]: Moment | null
			[OutboundFormFields.supplierLocationId]: LabelValue<number> | null
			[OutboundFormFields.transporterId]: LabelValue<number> | null
			[OutboundFormFields.trailerStatus]: string
		}
	}
	namespace Request {
		interface Generic {
			[OutboundFormFields.processingCenterId]: number | null
			[OutboundFormFields.trailerNumber]: BasicString
			[OutboundFormFields.sealNumber]: BasicString
			[OutboundFormFields.exportationNumber]: BasicString
			[OutboundFormFields.departureDate]: BasicString
			[OutboundFormFields.supplierLocationId]: number | null
			[OutboundFormFields.transporterId]: number | null
		}
	}

	interface Detailed {
		id: number
		departureDate: string
		exportationNumber: string
		processingCenter: Entity
		status: Entity
		supplierLocation: Entity
		trailerNumber: string
		transporter: Entity
		billBales: boolean
		baleBillingReport: boolean
		itemCharge: string | null
	}

	interface ProcessingCenterBale extends FinishedGoodResponses {
		finishedGoodResponses: FinishedGoodResponses[]
		totalWeight: number
	}

	interface FinishedGoodResponses {
		baleDate: string
		baleNumber: string
		folioNumber: string
		inboundSealNumber: string
		item: Entity
		productType: Entity
		kilograms: number
	}

	namespace Status {
		namespace Form {
			interface Add {
				departureDate: Moment | null
			}
		}
		namespace Request {
			interface Add {
				departureDate: BasicString
				statusActionId: number
			}
		}
	}

	namespace Filter {
		interface Config {
			[OutboundTrailerFilterFields.processingCenterId]: ConfigFilterItem<string, string>
			[OutboundTrailerFilterFields.statusId]: ConfigFilterItem<string, string>
			[OutboundTrailerFilterFields.departureDate]: ConfigFilterItem<string, null>
		}
	}

	namespace Search {
		namespace Suggestions {
			interface Result {
				[OutboundTrailerSearchResultFields.searchByTrailerNumber]: Entity[]
				[OutboundTrailerSearchResultFields.searchBySealNumber]: Entity[]
				[OutboundTrailerSearchResultFields.searchByExportationNumber]: Entity[]
				[OutboundTrailerSearchResultFields.searchBySupplierLocationId]: Entity[]
				[OutboundTrailerSearchResultFields.searchBySupplierLocationName]: Entity['name'][]
				[OutboundTrailerSearchResultFields.searchByFolioNumber]: Entity[]
			}

			interface Config {
				[OutboundTrailerSearchResultFields.searchByTrailerNumber]: ConfigItem<OutboundTrailerSearchResultFields>
				[OutboundTrailerSearchResultFields.searchBySealNumber]: ConfigItem<OutboundTrailerSearchResultFields>
				[OutboundTrailerSearchResultFields.searchByExportationNumber]: ConfigItem<OutboundTrailerSearchResultFields>
				[OutboundTrailerSearchResultFields.searchBySupplierLocationId]: ConfigItem<OutboundTrailerSearchResultFields>
				[OutboundTrailerSearchResultFields.searchBySupplierLocationName]: ConfigItem<OutboundTrailerSearchResultFields>
				[OutboundTrailerSearchResultFields.searchByFolioNumber]: ConfigItem<OutboundTrailerSearchResultFields>
			}
		}
	}

	namespace LoadDetails {
		interface TableConfig<SelectFieldNames extends string> {
			selects: Record<
				SelectFieldNames,
				{
					options: LabelValue<number>[]
				}
			>
		}
		type FieldType = Record<LoadDetailsTableFields, Key | null>

		namespace Bale {
			interface Basic {
				id: number
				processingCenter: Entity
				baleNumber: string
				folioNumber: string
				partialNumber: string
				inboundSealNumber: string
				baleDate: string
				serviceSite: Entity
				pounds: number
				kilograms: number
				outboundSealNumber: string
				departureDate: string
				item: Entity
				materialName: ProcessingCenter.MaterialType
				processingCenterLocation: Entity
				isActive: true
			}
			interface Item {
				item: Entity
				isMatchedUoM: boolean
			}
		}

		namespace Form {
			interface ExtractedLabelValue extends LabelValue {
				isMatchedUoM: boolean
			}
			interface SelectedBaleItem {
				[LoadDetailsTableFields.baleId]: number
				[LoadDetailsTableFields.itemId]: LabelValue | null
				[LoadDetailsTableFields.isMatchedUoM]: boolean
				[LoadDetailsTableFields.kilograms]: number
			}

			interface BaleItems extends Basic, SelectedBaleItem {}

			interface Add {
				[LoadDetailsFields.items]: BaleItems[]
			}
		}
		namespace Request {
			interface Add {
				items: {
					[LoadDetailsTableFields.baleId]: number
					[LoadDetailsTableFields.itemId]: BasicString
				}[]
			}
		}

		namespace Search {
			namespace Suggestions {
				interface Result {
					[LoadDetailsSearchFields.baleNumbers]: string[]
					[LoadDetailsSearchFields.folioNumbers]: string[]
					[LoadDetailsSearchFields.inboundSealNumbers]: string[]
				}

				interface Config {
					[LoadDetailsSearchFields.baleNumbers]: ConfigItem<LoadDetailsSearchFields>
					[LoadDetailsSearchFields.folioNumbers]: ConfigItem<LoadDetailsSearchFields>
					[LoadDetailsSearchFields.inboundSealNumbers]: ConfigItem<LoadDetailsSearchFields>
				}
			}
		}
		namespace Filter {
			interface Config {
				[LoadDetailsFilterFields.serviceSiteId]: ConfigFilterItem<string, null>
				[LoadDetailsFilterFields.materialId]: ConfigFilterItem<string, null>
				[LoadDetailsFilterFields.baleDate]: ConfigFilterItem<string, null>
			}
		}
	}
	namespace BaleBilling {
		interface Invalid {
			message: string
			items: Entity[]
		}
	}
}
